/**
 * Vektör arama paneli — bölüm / birim / persona ölçeğinde ortak bileşen.
 *
 * Neden ayrı bir bileşen: tur zamanı retrieval (`RagService.search_library`)
 * zaten her turda otomatik çalışıyor, ama operatör "bu havuzda X konusunda
 * bir şey var mıydı?" sorusunu bir tartışma başlatmadan yanıtlamak istiyor.
 * Üç çağıran (`ArchiveTab`, `UnitDocumentsModal`, `CollectionCard`) aynı
 * UI'ı, sadece arama fonksiyonunun kapsamı (bölüm/birim/persona) farklı
 * olacak şekilde kullanır — kapsam çözümlemesi backend'de (`routes_persona_
 * library.py` search_* rotaları), burada değil.
 */
import { useState } from "react";
import type { LibrarySearchHit } from "../../domain/personaLibrary";
import { LibraryApiError, documentDownloadUrl } from "../../transport/personaLibraryClient";
import { Btn, Chip, ErrorBanner, TextInput } from "./ui";

function scoreTone(score: number): "green" | "amber" | "neutral" {
  if (score >= 0.6) return "green";
  if (score >= 0.4) return "amber";
  return "neutral";
}

export function LibrarySearchPanel({
  search,
  placeholder = "Anlamsal arama — konu, isim, terim…",
  emptyHint,
}: {
  search: (query: string) => Promise<LibrarySearchHit[]>;
  placeholder?: string;
  emptyHint?: string;
}) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<LibrarySearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      setHits(await search(q));
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Arama başarısız.");
      setHits(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-2.5">
      <div className="flex gap-2">
        <div className="flex-1">
          <TextInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void run();
            }}
            placeholder={placeholder}
            maxLength={500}
          />
        </div>
        <Btn tone="primary" onClick={() => void run()} loading={loading} disabled={!query.trim()}>
          Ara
        </Btn>
        {hits !== null && (
          <Btn
            tone="ghost"
            onClick={() => {
              setHits(null);
              setQuery("");
              setError(null);
            }}
          >
            Temizle
          </Btn>
        )}
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {hits !== null && (
        <div className="space-y-1.5">
          {hits.length === 0 ? (
            <p className="py-3 text-center text-[11.5px] text-slate-600">
              {emptyHint ?? "Bu ifadeyle eşleşen parça bulunamadı. Farklı bir terim dene."}
            </p>
          ) : (
            <>
              <p className="text-[10.5px] text-slate-600">
                {hits.length} parça eşleşti — en alakalı vektöre göre sıralı.
              </p>
              {hits.map((h, i) => (
                <div
                  key={`${h.documentId}-${h.chunkIndex}-${i}`}
                  className="rounded-lg border border-white/[0.07] bg-black/25 p-2.5"
                >
                  <div className="mb-1 flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-[11.5px] font-medium text-slate-300" title={h.filename}>
                      📄 {h.filename}
                    </span>
                    <Chip tone={scoreTone(h.score)}>%{Math.round(h.score * 100)} eşleşme</Chip>
                    <a
                      href={documentDownloadUrl(h.documentId)}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-[10.5px] text-violet-300/90 hover:text-violet-200 hover:underline"
                    >
                      İndir
                    </a>
                  </div>
                  <p className="text-[12px] leading-relaxed text-slate-400">{h.text}</p>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
